/**
 * ThemeToggle — three-way segmented control in the top bar.
 *
 * Sprint AO. Sun / Monitor / Moon icons map to light / system / dark.
 * The control is a single rounded pill with the active option
 * highlighted; mirrors the visual language of the other top-bar
 * controls in AppShell.
 */

import { Monitor, Moon, Sun } from "lucide-react";
import { cn } from "@vseh/ui";

import { useT } from "../i18n";
import { useTheme, type ThemeChoice } from "./ThemeProvider";


const OPTIONS: {
  value: ThemeChoice;
  labelKey: string;
  icon: typeof Sun;
}[] = [
  { value: "light",  labelKey: "theme.option.light",  icon: Sun },
  { value: "system", labelKey: "theme.option.system", icon: Monitor },
  { value: "dark",   labelKey: "theme.option.dark",   icon: Moon },
];


export function ThemeToggle(): JSX.Element {
  const t = useT();
  const { choice, setChoice } = useTheme();
  return (
    <div
      role="radiogroup"
      aria-label={t("theme.aria")}
      className="flex h-8 items-center gap-0.5 rounded-md border border-base-200 bg-base-50 p-0.5"
    >
      {OPTIONS.map(({ value, labelKey, icon: Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={choice === value}
          title={t(labelKey)}
          onClick={() => setChoice(value)}
          className={cn(
            "grid size-7 place-items-center rounded-sm transition-colors",
            choice === value
              ? "bg-surface text-base-900 shadow-xs"
              : "text-base-500 hover:text-base-700",
          )}
        >
          <Icon aria-hidden="true" className="size-3.5" />
        </button>
      ))}
    </div>
  );
}
